import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import IcCross from '@/assets/icons/Cross.svg?react';
import Button from '@/components/Button';
import Spinner from '@/components/Spinner';
import useAuth from '@/components/UserContext';
import useApi from '@/hooks/useApi';
import useApp from '@/pages/App.context';
import {
  readRememberedProvider,
  resolveProviderName,
} from './lastProvider.util';
import { isWebidOutcome } from './webidForm.types';
import styles from './KycVerify.module.scss';

/**
 * KYC return page. Reached from two providers with different completion shapes:
 *
 * 1. eID Easy — OAuth redirect with ?code=... in the URL. The browser
 *    posts the code to /kyc/verify and the backend resolves the user.
 *
 * 2. WebID — browser redirects here with ?webid=success|declined|mismatch|cancelled.
 *    The verification result is delivered to the backend via webhook
 *    (POST /api/v1/kyc/webid/webhook) which writes user.kyc. We poll
 *    users/me until kyc flips to true, or surface an error if the webid
 *    query param indicates a non-success terminal state.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

const KycVerify = () => {
  const { refetchMe, hasKYC } = useAuth();
  const { t } = useTranslation(['common', 'onboarding']);
  const { setAppTitle, setHeadline } = useApp();
  const navigate = useNavigate();

  const [verifyKyc, { error, response, loading }] = useApi('kyc.verify');
  const [loadOrganization] = useApi('organization.me');

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const webidOutcomeParam = params.get('webid');
  const webidOutcome = isWebidOutcome(webidOutcomeParam)
    ? webidOutcomeParam
    : null;
  const isWebidFlow = webidOutcome !== null;
  const isEideasyFlow = !!code && !isWebidFlow;
  const hasReturnParams = isWebidFlow || isEideasyFlow;

  const [pollExpired, setPollExpired] = useState(false);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    setAppTitle(t('common:menu.kycVerification'));
    setHeadline({
      title: t('onboarding:kycSelection.headline.title'),
      subtitle: t('onboarding:kycSelection.headline.subtitle'),
    });
  }, [setAppTitle, setHeadline, t]);

  // eID Easy flow: post the OAuth code to /kyc/verify exactly once.
  useEffect(() => {
    if (isEideasyFlow && code) {
      void verifyKyc({ type: 'eideasy', code });
    }
  }, [verifyKyc, isEideasyFlow, code]);

  // eID Easy flow: on successful verify, refresh user/org and bounce.
  useEffect(() => {
    if (!isEideasyFlow || !response?.success) {
      return;
    }
    const finalise = async () => {
      await Promise.all([loadOrganization(), refetchMe()]);
      void navigate('/kyc/provider', {
        replace: true,
        state: { justVerified: true },
      });
    };
    void finalise();
  }, [isEideasyFlow, loadOrganization, navigate, refetchMe, response?.success]);

  // WebID flow: poll users/me until the webhook has written user.kyc, then bounce.
  useEffect(() => {
    if (!isWebidFlow || webidOutcome !== 'success') {
      return;
    }
    if (hasKYC) {
      const finalise = async () => {
        await loadOrganization();
        void navigate('/kyc/provider', {
          replace: true,
          state: { justVerified: true },
        });
      };
      void finalise();
      return;
    }

    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }

    const interval = setInterval(() => {
      const elapsed = Date.now() - (startedAtRef.current ?? Date.now());
      if (elapsed > POLL_TIMEOUT_MS) {
        clearInterval(interval);
        setPollExpired(true);
        return;
      }
      void refetchMe();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [
    isWebidFlow,
    webidOutcome,
    hasKYC,
    loadOrganization,
    navigate,
    refetchMe,
  ]);

  const eideasyHasError =
    isEideasyFlow && !loading && (!!error || (!!response && !response.success));
  const webidHasError =
    isWebidFlow && (webidOutcome !== 'success' || pollExpired);
  const showError = !hasReturnParams || eideasyHasError || webidHasError;

  if (showError) {
    return (
      <div className={styles.container}>
        <section className={styles.card}>
          <span className={styles.errorIcon} aria-hidden="true">
            <IcCross />
          </span>
          <h2 className={styles.title}>{t('onboarding:kycVerify.title')}</h2>
          <div className={styles.desc}>
            {t('onboarding:kycVerify.error.desc')}
          </div>
          <Button as="link" to="/kyc/provider" className={styles.cta}>
            {t('onboarding:kycVerify.error.retry')}
          </Button>
        </section>
      </div>
    );
  }

  const providerName = resolveProviderName(
    readRememberedProvider(),
    t('onboarding:kycVerified.card.fallbackProvider')
  );

  return (
    <div className={styles.container}>
      <section className={styles.card}>
        <h2 className={styles.title}>
          {t('onboarding:kycVerify.loading.title')}
        </h2>
        <div
          className={styles.loaderWrap}
          role="status"
          aria-label={t('onboarding:kyc.verifying')}
        >
          <Spinner />
        </div>
        <div className={styles.desc}>
          <Trans
            i18nKey="onboarding:kycVerify.loading.finalising"
            values={{ provider: providerName }}
            components={[<strong key="provider" />]}
          />
        </div>
        <div className={styles.subDesc}>
          {t('onboarding:kycVerify.loading.subtitle')}
        </div>
      </section>
    </div>
  );
};

export default KycVerify;
